// ── Dry Storage 3D ───────────────────────────────────────────────────────────────────────────
// A 3D CAD-style editor for dry boat storage inventory: rack tiers, yard/trailer slots, covered
// rows, boatyard storage. The dry-side analog of Slip Inventory, but the unit of work is a 3D
// box (position + heading + L/W/H) instead of a 2D polygon, because the binding constraint across
// a rack is vertical (tier clearance, rack load), not anything mappable in 2D.
// See docs/dry-storage-3d-spec.md for the full data model, API and phase plan, and
// docs/research/dry-storage-3d.md for the market research behind it.
//
// Scoped to the active project (same "marina_id = projectId" convention sonar-mosaic-editor and
// dock-builder use), persisted through services/slip-api/src/dry-storage.js, reachable at
// /api/slip-inventory/v1/dry-storage/... -- piggybacking on the existing slip-inventory proxy
// prefix the same way dock-builder's /dock-assemblies calls do, so no new proxy route was needed.
//
// Position is a project-local plan in feet (not lat/long yet -- Phase 3 in the spec). One
// three.js world unit = one foot, Y-up (height), X/Z the floor plane. Units are boxes; dragging
// moves a unit on the floor plane at its own height, Shift-dragging rotates it (heading, snapped
// to 5°) by the angle between the pointer and the unit's screen-projected center -- the same
// "drag to move, Shift-drag to turn" gesture services/ais-api/src/sandbox-objects.js uses for
// placing boats on the 2D/3D slip map, generalized from a preset model to a parametric box.
//
// The canvas is mounted once per project and never destroyed by a sidebar re-render (sidebars
// replace their own innerHTML freely; the canvas host div is untouched after the skeleton is
// built), same convention as sonar-mosaic-editor/index.js.

import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

const TOOL_ID = "dry-storage-3d";
const API = "/api/slip-inventory/v1/dry-storage";

const STORAGE_MODES = [
  { id: "rack", label: "Rack (tiered)" },
  { id: "yard", label: "Yard (uncovered)" },
  { id: "covered", label: "Covered row" },
  { id: "indoor", label: "Indoor" },
  { id: "boatyard", label: "Boatyard" },
  { id: "trailer_lot", label: "Trailer lot" },
];
const MODE_LABEL = Object.fromEntries(STORAGE_MODES.map((m) => [m.id, m.label]));
const MODE_COLOR = { rack: 0x38bdf8, yard: 0x84cc16, covered: 0xf59e0b, indoor: 0xa855f7, boatyard: 0xef4444, trailer_lot: 0x94a3b8 };
const STATUSES = ["operable", "under_construction", "blocked", "maintenance", "closed"];
const OCCUPANCIES = ["vacant", "reserved", "occupied", "in_transit"];
const SIZE_BAND_LABEL = { small: "Small (≤26 ft)", mid: "Mid (27–35 ft)", standard: "Standard (36–42 ft)", large: "Large (43 ft+)" };
const VALIDATION_RULE_LABEL = {
  missing_type: "Missing unit type",
  overlapping_cells: "Overlapping cells",
  outlier_dimensions: "Outlier dimensions",
  duplicate_unit_label: "Duplicate unit label",
  orphan_tier: "Orphan tier (gap below)",
  overweight_assignment: "Overweight assignment",
};

const DEG2RAD = Math.PI / 180;
const RAD2DEG = 180 / Math.PI;
const snap = (v, step) => Math.round(v / step) * step;

let _lastBroadcastProjectId = "";
document.addEventListener("hydrotools:project-changed", (e) => {
  _lastBroadcastProjectId = String(e.detail?.projectId || "");
});

function esc(v) {
  return String(v ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}
function fmt(v, d = 1) { return v === null || v === undefined || v === "" ? "—" : Number(v).toFixed(d); }

async function httpJson(method, path, body) {
  const r = await fetch(`${API}${path}`, {
    method,
    headers: body !== undefined ? { "content-type": "application/json" } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const j = await r.json().catch(() => ({ ok: false, error: `HTTP ${r.status}` }));
  if (!r.ok || !j.ok) throw Object.assign(new Error(j.error || `HTTP ${r.status}`), { status: r.status });
  return j;
}

function createRuntime(app) {
  let containerEl = null, canvasHostEl = null, leftHostEl = null, rightHostEl = null;
  let currentMarinaId = "";

  // Three.js scene, built once per mount.
  let renderer = null, scene = null, camera = null, controls = null, rafId = null, resizeObs = null;
  let hasFittedCamera = false;
  const unitMeshes = new Map(); // unit_id -> THREE.Mesh
  const raycaster = new THREE.Raycaster();
  const floorPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);

  let unitTypes = [];
  let units = [];
  const selectedIds = new Set();
  let primaryId = null;

  let summary = null;
  let validation = null;
  let loading = false, error = null, notice = null;

  let placing = false;
  let placeTypeId = "";
  let rackGen = { typeId: "", rows: 4, tiers: 3, rowGapFt: 1, tierGapFt: 1, building: "", rackPrefix: "R", originXFt: 0, originYFt: 0, headingDeg: 0 };
  let draftType = { name: "", storage_mode: "rack", length_ft: 24, width_ft: 10, height_ft: 9, max_weight_lbs: 8000, default_tier_count: 3 };
  let editingTypeId = null;

  let drag = null; // { unitId, kind: "move"|"rotate", startClientX, startClientY, startPos:{x,y}, startHeading, centerScreen }

  function selectedUnits() { return units.filter((u) => selectedIds.has(u.unit_id)); }
  function primaryUnit() { return units.find((u) => u.unit_id === primaryId) || null; }
  function unitTypeById(id) { return unitTypes.find((t) => t.type_id === id) || null; }

  // ── data loading ──────────────────────────────────────────────────────────────────────────
  async function loadAll() {
    if (!currentMarinaId) { unitTypes = []; units = []; summary = null; validation = null; rebuildScene(); renderSidebars(); return; }
    loading = true; error = null; renderSidebars();
    try {
      const [t, u] = await Promise.all([
        httpJson("GET", `/unit-types?marina_id=${encodeURIComponent(currentMarinaId)}`),
        httpJson("GET", `/units?marina_id=${encodeURIComponent(currentMarinaId)}`),
      ]);
      unitTypes = t.items; units = u.items;
      selectedIds.clear(); primaryId = null;
      rebuildScene();
      if (!hasFittedCamera && units.length) { fitCameraToUnits(); hasFittedCamera = true; }
      await refreshRollups();
    } catch (e) {
      error = e.message || String(e);
    } finally {
      loading = false; renderSidebars();
    }
  }

  async function refreshRollups() {
    try { summary = (await httpJson("GET", `/summary?marina_id=${encodeURIComponent(currentMarinaId)}`)); } catch { summary = null; }
    try { validation = (await httpJson("GET", `/validate?marina_id=${encodeURIComponent(currentMarinaId)}`)).issues; } catch { validation = null; }
    renderSidebars();
  }

  // ── three.js scene lifecycle ─────────────────────────────────────────────────────────────
  function ensureScene() {
    if (renderer || !canvasHostEl) return;
    const w = Math.max(200, Math.floor(canvasHostEl.clientWidth || 640));
    const h = Math.max(200, Math.floor(canvasHostEl.clientHeight || 480));

    renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
    renderer.setSize(w, h, false);
    renderer.domElement.style.width = "100%";
    renderer.domElement.style.height = "100%";
    renderer.domElement.style.display = "block";
    renderer.domElement.tabIndex = 0; // click-to-focus so arrow-key nudge / Delete only fire over the canvas
    canvasHostEl.appendChild(renderer.domElement);

    scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0b1220);

    camera = new THREE.PerspectiveCamera(50, w / h, 0.1, 6000);
    camera.position.set(80, 70, 110);

    controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.12;
    controls.target.set(0, 0, 0);
    controls.maxPolarAngle = Math.PI / 2 - 0.02;

    scene.add(new THREE.HemisphereLight(0x9dcbff, 0x33404f, 0.95));
    const sun = new THREE.DirectionalLight(0xffffff, 0.95);
    sun.position.set(60, 120, 40);
    scene.add(sun);

    const grid = new THREE.GridHelper(400, 100, 0x334155, 0x1e293b);
    scene.add(grid);
    const axes = new THREE.AxesHelper(8);
    scene.add(axes);

    renderer.domElement.addEventListener("pointerdown", onPointerDown);
    renderer.domElement.addEventListener("pointermove", onPointerMove);
    renderer.domElement.addEventListener("pointerup", onPointerUp);
    renderer.domElement.addEventListener("keydown", onKeyDown);

    if (typeof ResizeObserver !== "undefined") {
      resizeObs = new ResizeObserver(() => {
        if (!renderer || !camera || !canvasHostEl) return;
        const nw = Math.max(200, Math.floor(canvasHostEl.clientWidth || 640));
        const nh = Math.max(200, Math.floor(canvasHostEl.clientHeight || 480));
        renderer.setSize(nw, nh, false);
        camera.aspect = nw / nh;
        camera.updateProjectionMatrix();
      });
      resizeObs.observe(canvasHostEl);
    }

    const loop = () => { rafId = requestAnimationFrame(loop); controls.update(); renderer.render(scene, camera); };
    loop();

    rebuildScene();
  }

  function disposeScene() {
    if (rafId) cancelAnimationFrame(rafId);
    rafId = null;
    resizeObs?.disconnect(); resizeObs = null;
    for (const m of unitMeshes.values()) disposeMesh(m);
    unitMeshes.clear();
    controls?.dispose(); controls = null;
    renderer?.dispose();
    if (renderer?.domElement?.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement);
    renderer = null; scene = null; camera = null;
    hasFittedCamera = false;
  }

  function disposeMesh(m) {
    m.geometry?.dispose?.();
    m.material?.dispose?.();
    for (const c of m.children) { c.geometry?.dispose?.(); c.material?.dispose?.(); }
  }

  function meshForUnit(u) {
    const h = Number(u.height_ft) || 4;
    const geo = new THREE.BoxGeometry(Math.max(0.1, u.length_ft), h, Math.max(0.1, u.width_ft));
    const color = MODE_COLOR[u.storage_mode] ?? 0x94a3b8;
    const faded = u.status !== "operable";
    const opacity = faded ? 0.28 : u.occupancy === "occupied" ? 0.95 : u.occupancy === "reserved" ? 0.75 : 0.5;
    const mat = new THREE.MeshStandardMaterial({ color, transparent: true, opacity });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(u.pos_x_ft, u.pos_z_ft + h / 2, u.pos_y_ft);
    mesh.rotation.y = -u.heading_deg * DEG2RAD;
    mesh.userData.unitId = u.unit_id;
    const edges = new THREE.LineSegments(new THREE.EdgesGeometry(geo), new THREE.LineBasicMaterial({ color: u.locked ? 0xf59e0b : 0x0f172a }));
    mesh.add(edges);
    return mesh;
  }

  function rebuildScene() {
    if (!scene) return;
    for (const m of unitMeshes.values()) { scene.remove(m); disposeMesh(m); }
    unitMeshes.clear();
    for (const u of units) { const m = meshForUnit(u); scene.add(m); unitMeshes.set(u.unit_id, m); }
    applySelectionHighlight();
  }

  function updateUnitMesh(u) {
    const old = unitMeshes.get(u.unit_id);
    if (old && scene) { scene.remove(old); disposeMesh(old); }
    if (!scene) return;
    const m = meshForUnit(u);
    scene.add(m);
    unitMeshes.set(u.unit_id, m);
    applySelectionHighlight();
  }

  function applySelectionHighlight() {
    for (const [id, mesh] of unitMeshes) {
      const selected = selectedIds.has(id);
      mesh.material.emissive = new THREE.Color(selected ? (id === primaryId ? 0x38bdf8 : 0x0ea5e9) : 0x000000);
      mesh.material.emissiveIntensity = selected ? (id === primaryId ? 0.75 : 0.4) : 0;
    }
  }

  function fitCameraToUnits() {
    if (!units.length || !camera || !controls) return;
    const box = new THREE.Box3();
    for (const u of units) {
      const h = Number(u.height_ft) || 4;
      box.expandByPoint(new THREE.Vector3(u.pos_x_ft - u.length_ft, u.pos_z_ft, u.pos_y_ft - u.width_ft));
      box.expandByPoint(new THREE.Vector3(u.pos_x_ft + u.length_ft, u.pos_z_ft + h, u.pos_y_ft + u.width_ft));
    }
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    const radius = Math.max(20, size.length() * 0.6);
    controls.target.copy(center);
    camera.position.set(center.x + radius, center.y + radius * 0.8, center.z + radius);
    camera.updateProjectionMatrix();
  }

  // ── pointer interaction ──────────────────────────────────────────────────────────────────
  function pointerNdc(ev) {
    const rect = renderer.domElement.getBoundingClientRect();
    return new THREE.Vector2(((ev.clientX - rect.left) / rect.width) * 2 - 1, -((ev.clientY - rect.top) / rect.height) * 2 + 1);
  }
  function worldToScreenPx(vec3) {
    const v = vec3.clone().project(camera);
    const rect = renderer.domElement.getBoundingClientRect();
    return { x: (v.x * 0.5 + 0.5) * rect.width + rect.left, y: (1 - (v.y * 0.5 + 0.5)) * rect.height + rect.top };
  }
  function pickUnit(ev) {
    raycaster.setFromCamera(pointerNdc(ev), camera);
    const hits = raycaster.intersectObjects([...unitMeshes.values()], false);
    return hits.length ? hits[0].object.userData.unitId : null;
  }
  // Raycasts against a horizontal plane at the given height. Dragging a unit uses its OWN
  // pos_z_ft, not always ground level -- with a perspective camera, intersecting a rack tier's
  // drag at y=0 instead of its real height skews the mapping between cursor and object movement.
  function pickFloor(ev, atY = 0) {
    raycaster.setFromCamera(pointerNdc(ev), camera);
    const plane = atY === 0 ? floorPlane : new THREE.Plane(new THREE.Vector3(0, 1, 0), -atY);
    const target = new THREE.Vector3();
    return raycaster.ray.intersectPlane(plane, target) ? target : null;
  }

  function onPointerDown(ev) {
    renderer.domElement.focus();
    if (placing) {
      const p = pickFloor(ev);
      if (p) placeSingleUnit(snap(p.x, 0.5), snap(p.z, 0.5));
      return;
    }
    const unitId = pickUnit(ev);
    if (!unitId) {
      if (!ev.ctrlKey && !ev.metaKey) { selectedIds.clear(); primaryId = null; applySelectionHighlight(); renderSidebars(); }
      return;
    }
    if (ev.ctrlKey || ev.metaKey) {
      if (selectedIds.has(unitId)) selectedIds.delete(unitId); else selectedIds.add(unitId);
      primaryId = unitId;
    } else if (!selectedIds.has(unitId)) {
      selectedIds.clear(); selectedIds.add(unitId); primaryId = unitId;
    } else {
      primaryId = unitId;
    }
    applySelectionHighlight();
    renderSidebars();

    const u = units.find((x) => x.unit_id === unitId);
    if (!u || u.locked) return;
    const mesh = unitMeshes.get(unitId);
    if (ev.shiftKey) {
      drag = { unitId, kind: "rotate", startHeading: u.heading_deg, centerScreen: worldToScreenPx(mesh.position), startClientX: ev.clientX, startClientY: ev.clientY };
    } else {
      drag = { unitId, kind: "move", startPos: { x: u.pos_x_ft, y: u.pos_y_ft }, planeY: u.pos_z_ft, startFloorPt: pickFloor(ev, u.pos_z_ft) };
    }
  }

  function onPointerMove(ev) {
    if (!drag) return;
    const u = units.find((x) => x.unit_id === drag.unitId);
    const mesh = unitMeshes.get(drag.unitId);
    if (!u || !mesh) return;
    if (drag.kind === "move") {
      const p = pickFloor(ev, drag.planeY);
      if (!p || !drag.startFloorPt) return;
      u.pos_x_ft = snap(drag.startPos.x + (p.x - drag.startFloorPt.x), 0.5);
      u.pos_y_ft = snap(drag.startPos.y + (p.z - drag.startFloorPt.z), 0.5);
      mesh.position.x = u.pos_x_ft; mesh.position.z = u.pos_y_ft;
    } else {
      const a0 = Math.atan2(drag.startClientY - drag.centerScreen.y, drag.startClientX - drag.centerScreen.x);
      const a1 = Math.atan2(ev.clientY - drag.centerScreen.y, ev.clientX - drag.centerScreen.x);
      const deltaDeg = (a1 - a0) * RAD2DEG;
      u.heading_deg = snap(((drag.startHeading - deltaDeg) % 360 + 360) % 360, 5);
      mesh.rotation.y = -u.heading_deg * DEG2RAD;
    }
    renderRightInspectorOnly();
  }

  async function onPointerUp() {
    if (!drag) return;
    const u = units.find((x) => x.unit_id === drag.unitId);
    const d = drag; drag = null;
    if (!u) return;
    try {
      const patch = d.kind === "move" ? { pos_x_ft: u.pos_x_ft, pos_y_ft: u.pos_y_ft } : { heading_deg: u.heading_deg };
      const r = await httpJson("PATCH", `/units/${u.unit_id}`, patch);
      applyServerUnit(r.item);
    } catch (e) {
      error = `Save failed: ${e.message}`; renderSidebars();
    }
  }

  function onKeyDown(ev) {
    if (ev.target !== renderer?.domElement) return;
    if (ev.key === "Delete" || ev.key === "Backspace") { ev.preventDefault(); deleteSelected(); return; }
    if (ev.key === "Escape") { selectedIds.clear(); primaryId = null; applySelectionHighlight(); renderSidebars(); return; }
    const u = primaryUnit();
    if (!u || u.locked) return;
    const step = ev.shiftKey ? 5 : 0.5;
    let moved = false;
    if (ev.key === "ArrowLeft") { u.pos_x_ft -= step; moved = true; }
    if (ev.key === "ArrowRight") { u.pos_x_ft += step; moved = true; }
    if (ev.key === "ArrowUp") { u.pos_y_ft -= step; moved = true; }
    if (ev.key === "ArrowDown") { u.pos_y_ft += step; moved = true; }
    if (!moved) return;
    ev.preventDefault();
    const mesh = unitMeshes.get(u.unit_id);
    if (mesh) { mesh.position.x = u.pos_x_ft; mesh.position.z = u.pos_y_ft; }
    renderRightInspectorOnly();
    httpJson("PATCH", `/units/${u.unit_id}`, { pos_x_ft: u.pos_x_ft, pos_y_ft: u.pos_y_ft }).then((r) => applyServerUnit(r.item)).catch((e) => { error = e.message; renderSidebars(); });
  }

  function applyServerUnit(item) {
    const idx = units.findIndex((x) => x.unit_id === item.unit_id);
    if (idx >= 0) units[idx] = item; else units.push(item);
    updateUnitMesh(item);
    refreshRollups();
  }

  // ── commands ─────────────────────────────────────────────────────────────────────────────
  function nextLabel(prefix) {
    const used = new Set(units.map((u) => u.unit_label).filter(Boolean));
    let n = units.length + 1;
    while (used.has(`${prefix}-${n}`)) n++;
    return `${prefix}-${n}`;
  }

  async function placeSingleUnit(xFt, yFt) {
    const t = unitTypeById(placeTypeId);
    if (!t) { error = "Pick a unit type first."; renderSidebars(); return; }
    const prefix = (t.name || t.storage_mode).replace(/\s+/g, "").slice(0, 4).toUpperCase();
    try {
      const r = await httpJson("POST", "/units", {
        marina_id: currentMarinaId, created_by_email: app?.auth?.email || null,
        units: [{
          type_id: t.type_id, storage_mode: t.storage_mode, tier_level: 0,
          pos_x_ft: xFt, pos_y_ft: yFt, pos_z_ft: 0, heading_deg: 0,
          length_ft: t.length_ft, width_ft: t.width_ft, height_ft: t.height_ft, max_weight_lbs: t.max_weight_lbs,
          unit_label: nextLabel(prefix), source: "manual",
        }],
      });
      units.push(...r.items);
      rebuildScene();
      placing = false;
      await refreshRollups();
    } catch (e) { error = `Placement failed: ${e.message}`; renderSidebars(); }
  }

  async function runRackGenerator() {
    const t = unitTypeById(rackGen.typeId);
    if (!t) { error = "Pick a unit type for the rack first."; renderSidebars(); return; }
    const rows = Math.max(1, Math.min(200, Math.round(Number(rackGen.rows) || 1)));
    const tiers = Math.max(1, Math.min(50, Math.round(Number(rackGen.tiers) || 1)));
    if (rows * tiers > 2000) { error = "That rack would place more than 2000 units at once -- reduce rows or tiers."; renderSidebars(); return; }
    const rad = Number(rackGen.headingDeg || 0) * DEG2RAD;
    const rackLabel = `${rackGen.rackPrefix || "R"}-${Math.random().toString(36).slice(2, 7)}`;
    const newUnits = [];
    for (let row = 0; row < rows; row++) {
      const along = row * (t.length_ft + Number(rackGen.rowGapFt || 0));
      const x = Number(rackGen.originXFt || 0) + along * Math.cos(rad);
      const y = Number(rackGen.originYFt || 0) + along * Math.sin(rad);
      for (let tier = 0; tier < tiers; tier++) {
        const z = tier * ((t.height_ft || 9) + Number(rackGen.tierGapFt || 0));
        newUnits.push({
          type_id: t.type_id, storage_mode: t.storage_mode, building: rackGen.building || null,
          row_label: `Row ${row + 1}`, rack_label: rackLabel, tier_level: tier,
          unit_label: `${rackLabel}-R${row + 1}T${tier + 1}`,
          pos_x_ft: x, pos_y_ft: y, pos_z_ft: z, heading_deg: Number(rackGen.headingDeg || 0),
          length_ft: t.length_ft, width_ft: t.width_ft, height_ft: t.height_ft, max_weight_lbs: t.max_weight_lbs,
          source: "generated",
        });
      }
    }
    try {
      const r = await httpJson("POST", "/units", { marina_id: currentMarinaId, created_by_email: app?.auth?.email || null, units: newUnits });
      units.push(...r.items);
      rebuildScene();
      notice = `Placed ${r.items.length} units (${rows} rows × ${tiers} tiers) as rack "${rackLabel}".`;
      await refreshRollups();
    } catch (e) { error = `Rack generator failed: ${e.message}`; renderSidebars(); }
  }

  async function duplicateRun() {
    const u = primaryUnit();
    if (!u || u.locked) return;
    const rad = u.heading_deg * DEG2RAD;
    const dx = (u.length_ft + 1) * Math.cos(rad);
    const dy = (u.length_ft + 1) * Math.sin(rad);
    await duplicateUnit(u, { pos_x_ft: u.pos_x_ft + dx, pos_y_ft: u.pos_y_ft + dy, unit_label: u.unit_label ? `${u.unit_label}-dup` : null });
  }
  async function duplicateStack() {
    const u = primaryUnit();
    if (!u || u.locked) return;
    await duplicateUnit(u, { tier_level: u.tier_level + 1, pos_z_ft: u.pos_z_ft + (u.height_ft || 9) + 1, unit_label: u.unit_label ? `${u.unit_label}-T${u.tier_level + 2}` : null });
  }
  async function duplicateUnit(u, overrides) {
    try {
      const copy = {
        type_id: u.type_id, storage_mode: u.storage_mode, building: u.building, row_label: u.row_label, rack_label: u.rack_label,
        tier_level: u.tier_level, unit_label: u.unit_label, pos_x_ft: u.pos_x_ft, pos_y_ft: u.pos_y_ft, pos_z_ft: u.pos_z_ft,
        heading_deg: u.heading_deg, length_ft: u.length_ft, width_ft: u.width_ft, height_ft: u.height_ft, max_weight_lbs: u.max_weight_lbs,
        covered_yn: u.covered_yn, indoor_yn: u.indoor_yn, source: u.source, ...overrides,
      };
      const r = await httpJson("POST", "/units", { marina_id: currentMarinaId, created_by_email: app?.auth?.email || null, units: [copy] });
      units.push(...r.items);
      selectedIds.clear(); selectedIds.add(r.items[0].unit_id); primaryId = r.items[0].unit_id;
      rebuildScene();
      await refreshRollups();
    } catch (e) { error = `Duplicate failed: ${e.message}`; renderSidebars(); }
  }

  async function toggleLockSelected() {
    const sel = selectedUnits();
    if (!sel.length) return;
    const lockTo = !sel.every((u) => u.locked);
    try {
      for (const u of sel) { const r = await httpJson("PATCH", `/units/${u.unit_id}`, { locked: lockTo }); applyServerUnit(r.item); }
    } catch (e) { error = `Lock failed: ${e.message}`; renderSidebars(); }
  }

  async function deleteSelected() {
    const sel = selectedUnits();
    if (!sel.length) return;
    const deletable = sel.filter((u) => !u.locked);
    const lockedCount = sel.length - deletable.length;
    if (!deletable.length) { error = "All selected units are locked -- unlock them first."; renderSidebars(); return; }
    const skipNote = lockedCount ? ` (${lockedCount} locked unit${lockedCount === 1 ? "" : "s"} will be skipped)` : "";
    if (!window.confirm(`Delete ${deletable.length} unit${deletable.length === 1 ? "" : "s"}${skipNote}? This can't be undone.`)) return;
    try {
      for (const u of deletable) await httpJson("DELETE", `/units/${u.unit_id}`);
      const removed = new Set(deletable.map((u) => u.unit_id));
      units = units.filter((u) => !removed.has(u.unit_id));
      selectedIds.clear(); primaryId = null;
      rebuildScene();
      await refreshRollups();
    } catch (e) { error = `Delete failed: ${e.message}`; renderSidebars(); }
  }

  async function saveInspectorField(unitId, field, value) {
    try {
      const r = await httpJson("PATCH", `/units/${unitId}`, { [field]: value });
      applyServerUnit(r.item);
    } catch (e) { error = `Save failed: ${e.message}`; renderSidebars(); }
  }

  // ── unit type library ────────────────────────────────────────────────────────────────────
  async function submitDraftType() {
    const t = draftType;
    if (!t.name.trim()) { error = "Type name is required."; renderSidebars(); return; }
    try {
      if (editingTypeId) {
        const r = await httpJson("PATCH", `/unit-types/${editingTypeId}`, t);
        const idx = unitTypes.findIndex((x) => x.type_id === editingTypeId);
        if (idx >= 0) unitTypes[idx] = r.item;
      } else {
        const r = await httpJson("POST", "/unit-types", { marina_id: currentMarinaId, ...t });
        unitTypes.push(r.item);
      }
      editingTypeId = null;
      draftType = { name: "", storage_mode: "rack", length_ft: 24, width_ft: 10, height_ft: 9, max_weight_lbs: 8000, default_tier_count: 3 };
      renderSidebars();
    } catch (e) { error = `Save type failed: ${e.message}`; renderSidebars(); }
  }
  function startEditType(id) {
    const t = unitTypeById(id);
    if (!t) return;
    editingTypeId = id;
    draftType = { name: t.name, storage_mode: t.storage_mode, length_ft: t.length_ft, width_ft: t.width_ft, height_ft: t.height_ft ?? "", max_weight_lbs: t.max_weight_lbs ?? "", default_tier_count: t.default_tier_count ?? 1 };
    renderSidebars();
  }
  async function deleteType(id) {
    if (!window.confirm("Delete this unit type? Units already using it keep their own dimensions.")) return;
    try { await httpJson("DELETE", `/unit-types/${id}`); unitTypes = unitTypes.filter((t) => t.type_id !== id); renderSidebars(); } catch (e) { error = e.message; renderSidebars(); }
  }

  // ── sidebars ─────────────────────────────────────────────────────────────────────────────
  function renderSkeleton() {
    containerEl.innerHTML = `
      <div class="small" style="color:var(--muted);padding-bottom:8px;">
        Draw dry stack racks, yard/trailer slots, covered rows and boatyard storage in 3D and quantify
        inventory, the dry-side counterpart to Slip Inventory. Click to select · Ctrl/Cmd-click to
        multi-select · drag to move · Shift-drag to rotate (5° snap) · arrow keys nudge · Delete removes.
      </div>
      ${!currentMarinaId ? `<div class="small" style="color:#f59e0b;padding:8px 0;">Select a project first.</div>` : `
        <div style="display:grid;grid-template-columns:300px 1fr 300px;gap:10px;height:680px;">
          <div data-left-host style="overflow:auto;display:flex;flex-direction:column;gap:10px;"></div>
          <div data-canvas-host style="border:1px solid rgba(148,163,184,0.15);border-radius:4px;overflow:hidden;background:#020617;outline:none;"></div>
          <div data-right-host style="overflow:auto;display:flex;flex-direction:column;gap:10px;"></div>
        </div>
      `}
    `;
    if (currentMarinaId) {
      canvasHostEl = containerEl.querySelector("[data-canvas-host]");
      leftHostEl = containerEl.querySelector("[data-left-host]");
      rightHostEl = containerEl.querySelector("[data-right-host]");
      ensureScene();
      renderSidebars();
    }
  }

  function renderSidebars() { renderLeft(); renderRight(); }
  function renderRightInspectorOnly() {
    const host = rightHostEl?.querySelector("[data-inspector-host]");
    if (host) host.innerHTML = inspectorHtml();
  }

  function typeOptionsHtml(selectedId) {
    return `<option value="">— choose a type —</option>` + unitTypes.map((t) =>
      `<option value="${esc(t.type_id)}" ${t.type_id === selectedId ? "selected" : ""}>${esc(t.name)} (${MODE_LABEL[t.storage_mode] || t.storage_mode}, ${fmt(t.length_ft, 0)}×${fmt(t.width_ft, 0)} ft)</option>`,
    ).join("");
  }

  function typeLibraryHtml() {
    const rows = unitTypes.map((t) => `
      <div style="display:flex;align-items:center;gap:6px;padding:4px 0;border-bottom:1px solid rgba(148,163,184,0.08);">
        <span style="flex:0 0 auto;width:8px;height:8px;border-radius:2px;background:#${(MODE_COLOR[t.storage_mode] ?? 0x94a3b8).toString(16).padStart(6, "0")};"></span>
        <div style="flex:1;min-width:0;">
          <div class="small" style="font-weight:600;">${esc(t.name)}</div>
          <div class="small" style="color:var(--muted);">${MODE_LABEL[t.storage_mode] || t.storage_mode} · ${fmt(t.length_ft, 0)}×${fmt(t.width_ft, 0)}×${fmt(t.height_ft, 0)} ft${t.max_weight_lbs ? ` · ${fmt(t.max_weight_lbs, 0)} lb` : ""}</div>
        </div>
        <button type="button" class="btn" data-edit-type="${esc(t.type_id)}" style="font-size:10px;padding:2px 6px;">Edit</button>
        <button type="button" class="btn" data-delete-type="${esc(t.type_id)}" style="font-size:10px;padding:2px 6px;">✕</button>
      </div>`).join("") || `<div class="small" style="color:var(--muted);">No unit types yet.</div>`;
    return `
      <div style="border:1px solid rgba(148,163,184,0.18);border-radius:8px;overflow:hidden;">
        <div style="padding:7px 10px;background:rgba(2,6,23,0.5);border-bottom:1px solid rgba(148,163,184,0.12);"><span class="small" style="font-weight:600;">UNIT TYPES</span></div>
        <div style="padding:8px 10px;max-height:160px;overflow:auto;">${rows}</div>
        <div style="padding:8px 10px;border-top:1px solid rgba(148,163,184,0.1);display:flex;flex-direction:column;gap:4px;">
          <input id="dt-name" type="text" placeholder="Name (e.g. 40ft Rack Bay)" value="${esc(draftType.name)}" />
          <select id="dt-mode">${STORAGE_MODES.map((m) => `<option value="${m.id}" ${m.id === draftType.storage_mode ? "selected" : ""}>${m.label}</option>`).join("")}</select>
          <div style="display:flex;gap:4px;">
            <input id="dt-length" type="number" step="0.5" placeholder="L ft" value="${esc(draftType.length_ft)}" style="width:0;flex:1;" />
            <input id="dt-width" type="number" step="0.5" placeholder="W ft" value="${esc(draftType.width_ft)}" style="width:0;flex:1;" />
            <input id="dt-height" type="number" step="0.5" placeholder="H ft" value="${esc(draftType.height_ft)}" style="width:0;flex:1;" />
          </div>
          <div style="display:flex;gap:4px;">
            <input id="dt-weight" type="number" step="100" placeholder="Max weight lb" value="${esc(draftType.max_weight_lbs)}" style="width:0;flex:1;" />
            <input id="dt-tiers" type="number" step="1" min="1" placeholder="Default tiers" value="${esc(draftType.default_tier_count)}" style="width:0;flex:1;" />
          </div>
          <button id="dt-submit" type="button" class="btn primary">${editingTypeId ? "Save Type" : "Add Type"}</button>
          ${editingTypeId ? `<button id="dt-cancel" type="button" class="btn">Cancel Edit</button>` : ""}
        </div>
      </div>`;
  }

  function rackGenHtml() {
    return `
      <div style="border:1px solid rgba(148,163,184,0.18);border-radius:8px;overflow:hidden;">
        <div style="padding:7px 10px;background:rgba(2,6,23,0.5);border-bottom:1px solid rgba(148,163,184,0.12);"><span class="small" style="font-weight:600;">RACK GENERATOR</span></div>
        <div style="padding:8px 10px;display:flex;flex-direction:column;gap:4px;">
          <select id="rg-type">${typeOptionsHtml(rackGen.typeId)}</select>
          <div style="display:flex;gap:4px;">
            <input id="rg-rows" type="number" min="1" placeholder="Rows" value="${esc(rackGen.rows)}" style="width:0;flex:1;" />
            <input id="rg-tiers" type="number" min="1" placeholder="Tiers" value="${esc(rackGen.tiers)}" style="width:0;flex:1;" />
          </div>
          <div style="display:flex;gap:4px;">
            <input id="rg-rowgap" type="number" step="0.5" placeholder="Row gap ft" value="${esc(rackGen.rowGapFt)}" style="width:0;flex:1;" />
            <input id="rg-tiergap" type="number" step="0.5" placeholder="Tier gap ft" value="${esc(rackGen.tierGapFt)}" style="width:0;flex:1;" />
          </div>
          <input id="rg-building" type="text" placeholder="Building / area (optional)" value="${esc(rackGen.building)}" />
          <div style="display:flex;gap:4px;">
            <input id="rg-ox" type="number" step="0.5" placeholder="Origin X ft" value="${esc(rackGen.originXFt)}" style="width:0;flex:1;" />
            <input id="rg-oy" type="number" step="0.5" placeholder="Origin Y ft" value="${esc(rackGen.originYFt)}" style="width:0;flex:1;" />
            <input id="rg-heading" type="number" step="5" placeholder="Heading °" value="${esc(rackGen.headingDeg)}" style="width:0;flex:1;" />
          </div>
          <button id="rg-run" type="button" class="btn primary">Generate Rack</button>
          <div class="small" style="color:var(--muted);">Places rows × tiers units in one action, grouped under one rack label.</div>
        </div>
      </div>`;
  }

  function placeToolHtml() {
    return `
      <div style="border:1px solid rgba(148,163,184,0.18);border-radius:8px;overflow:hidden;">
        <div style="padding:7px 10px;background:rgba(2,6,23,0.5);border-bottom:1px solid rgba(148,163,184,0.12);"><span class="small" style="font-weight:600;">PLACE SINGLE UNIT</span></div>
        <div style="padding:8px 10px;display:flex;flex-direction:column;gap:4px;">
          <select id="pl-type">${typeOptionsHtml(placeTypeId)}</select>
          <button id="pl-toggle" type="button" class="btn ${placing ? "primary" : ""}">${placing ? "Click the floor to place…" : "Place on Floor"}</button>
        </div>
      </div>`;
  }

  function selectionToolsHtml() {
    const sel = selectedUnits();
    const allLocked = sel.length && sel.every((u) => u.locked);
    return `
      <div style="border:1px solid rgba(148,163,184,0.18);border-radius:8px;overflow:hidden;">
        <div style="padding:7px 10px;background:rgba(2,6,23,0.5);border-bottom:1px solid rgba(148,163,184,0.12);"><span class="small" style="font-weight:600;">SELECTION (${sel.length})</span></div>
        <div style="padding:8px 10px;display:flex;flex-wrap:wrap;gap:4px;">
          <button id="sel-dup-run" type="button" class="btn" ${sel.length !== 1 ? "disabled" : ""}>Duplicate Run</button>
          <button id="sel-dup-stack" type="button" class="btn" ${sel.length !== 1 ? "disabled" : ""}>Duplicate Stack</button>
          <button id="sel-lock" type="button" class="btn" ${sel.length ? "" : "disabled"}>${allLocked ? "Unlock" : "Lock"}</button>
          <button id="sel-delete" type="button" class="btn" ${sel.length ? "" : "disabled"} style="color:#ef4444;">Delete</button>
        </div>
      </div>`;
  }

  function renderLeft() {
    if (!leftHostEl) return;
    leftHostEl.innerHTML = `
      ${error ? `<div class="small" style="color:#ef4444;">${esc(error)} <button type="button" data-dismiss-error style="background:none;border:none;color:#ef4444;text-decoration:underline;cursor:pointer;">dismiss</button></div>` : ""}
      ${notice ? `<div class="small" style="color:#22c55e;">${esc(notice)}</div>` : ""}
      ${loading ? `<div class="small" style="color:var(--muted);">Loading…</div>` : ""}
      ${typeLibraryHtml()}
      ${rackGenHtml()}
      ${placeToolHtml()}
      ${selectionToolsHtml()}
    `;
    bindLeftEvents();
  }

  function inspectorHtml() {
    const u = primaryUnit();
    if (!u) return `<div class="small" style="color:var(--muted);padding:8px 0;">Select a unit to see and edit its details.</div>`;
    return `
      <div style="display:flex;flex-direction:column;gap:4px;">
        <div style="display:flex;align-items:center;gap:6px;">
          <span class="small" style="font-weight:600;">${esc(u.unit_label || u.unit_id.slice(0, 8))}</span>
          ${u.locked ? `<span class="small" style="color:#f59e0b;">locked</span>` : ""}
        </div>
        <label class="small">Label<input data-field="unit_label" type="text" value="${esc(u.unit_label || "")}" /></label>
        <label class="small">Mode<select data-field="storage_mode">${STORAGE_MODES.map((m) => `<option value="${m.id}" ${m.id === u.storage_mode ? "selected" : ""}>${m.label}</option>`).join("")}</select></label>
        <div style="display:flex;gap:4px;">
          <label class="small" style="flex:1;">Building<input data-field="building" type="text" value="${esc(u.building || "")}" /></label>
          <label class="small" style="flex:1;">Rack<input data-field="rack_label" type="text" value="${esc(u.rack_label || "")}" /></label>
        </div>
        <label class="small">Tier level<input data-field="tier_level" type="number" step="1" value="${esc(u.tier_level)}" /></label>
        <div style="display:flex;gap:4px;">
          <label class="small" style="flex:1;">L ft<input data-field="length_ft" type="number" step="0.5" value="${esc(u.length_ft)}" /></label>
          <label class="small" style="flex:1;">W ft<input data-field="width_ft" type="number" step="0.5" value="${esc(u.width_ft)}" /></label>
          <label class="small" style="flex:1;">H ft<input data-field="height_ft" type="number" step="0.5" value="${esc(u.height_ft ?? "")}" /></label>
        </div>
        <label class="small">Max weight lb<input data-field="max_weight_lbs" type="number" step="100" value="${esc(u.max_weight_lbs ?? "")}" /></label>
        <label class="small">Heading °<input data-field="heading_deg" type="number" step="5" value="${esc(u.heading_deg)}" /></label>
        <div style="display:flex;gap:4px;">
          <label class="small" style="flex:1;">Status<select data-field="status">${STATUSES.map((s) => `<option value="${s}" ${s === u.status ? "selected" : ""}>${s}</option>`).join("")}</select></label>
          <label class="small" style="flex:1;">Occupancy<select data-field="occupancy">${OCCUPANCIES.map((s) => `<option value="${s}" ${s === u.occupancy ? "selected" : ""}>${s}</option>`).join("")}</select></label>
        </div>
        <label class="small">Assigned vessel<input data-field="assigned_vessel_name" type="text" value="${esc(u.assigned_vessel_name || "")}" /></label>
        <label class="small">Assigned customer<input data-field="assigned_customer" type="text" value="${esc(u.assigned_customer || "")}" /></label>
        <label class="small">Notes<textarea data-field="notes" rows="2">${esc(u.notes || "")}</textarea></label>
        <div class="small" style="color:var(--muted);">Position: ${fmt(u.pos_x_ft)}, ${fmt(u.pos_y_ft)} ft, tier z ${fmt(u.pos_z_ft)} ft</div>
      </div>`;
  }

  function rollupHtml() {
    if (!summary) return `<div class="small" style="color:var(--muted);">No rollup yet.</div>`;
    const modeRows = Object.entries(summary.byStorageMode || {}).map(([k, v]) => `<div class="small" style="display:flex;justify-content:space-between;"><span>${MODE_LABEL[k] || k}</span><span>${v}</span></div>`).join("") || `<div class="small" style="color:var(--muted);">No units yet.</div>`;
    const bandRows = Object.entries(summary.bySizeBand || {}).map(([k, v]) => `<div class="small" style="display:flex;justify-content:space-between;"><span>${SIZE_BAND_LABEL[k] || k}</span><span>${v}</span></div>`).join("");
    return `
      <div class="small" style="font-weight:600;margin-bottom:4px;">${summary.totalUnits} unit${summary.totalUnits === 1 ? "" : "s"} · ${summary.occupancyPct}% occupied</div>
      <div style="margin-bottom:6px;">${modeRows}</div>
      <div class="small" style="color:var(--muted);margin-bottom:2px;">By size band</div>
      <div>${bandRows}</div>`;
  }

  function validationHtml() {
    if (!validation) return `<div class="small" style="color:var(--muted);">No validation run yet.</div>`;
    if (!validation.length) return `<div class="small" style="color:#22c55e;">No issues found.</div>`;
    const byRule = new Map();
    for (const issue of validation) {
      if (!byRule.has(issue.rule)) byRule.set(issue.rule, []);
      byRule.get(issue.rule).push(issue);
    }
    return [...byRule.entries()].map(([rule, issues]) => `
      <details style="margin-bottom:4px;">
        <summary class="small" style="cursor:pointer;color:#ef4444;">${esc(VALIDATION_RULE_LABEL[rule] || rule)} (${issues.length})</summary>
        <div style="padding:2px 0 2px 10px;">${issues.map((i) => `<div class="small" style="color:var(--muted);">${esc(i.detail)}</div>`).join("")}</div>
      </details>`).join("");
  }

  function renderRight() {
    if (!rightHostEl) return;
    rightHostEl.innerHTML = `
      <div style="border:1px solid rgba(148,163,184,0.18);border-radius:8px;overflow:hidden;">
        <div style="padding:7px 10px;background:rgba(2,6,23,0.5);border-bottom:1px solid rgba(148,163,184,0.12);"><span class="small" style="font-weight:600;">INSPECTOR</span></div>
        <div data-inspector-host style="padding:8px 10px;">${inspectorHtml()}</div>
      </div>
      <div style="border:1px solid rgba(148,163,184,0.18);border-radius:8px;overflow:hidden;">
        <div style="padding:7px 10px;background:rgba(2,6,23,0.5);border-bottom:1px solid rgba(148,163,184,0.12);"><span class="small" style="font-weight:600;">INVENTORY ROLLUP</span></div>
        <div style="padding:8px 10px;">${rollupHtml()}</div>
      </div>
      <div style="border:1px solid rgba(148,163,184,0.18);border-radius:8px;overflow:hidden;">
        <div style="padding:7px 10px;background:rgba(2,6,23,0.5);border-bottom:1px solid rgba(148,163,184,0.12);display:flex;align-items:center;gap:8px;">
          <span class="small" style="font-weight:600;flex:1;">VALIDATION</span>
          <button id="val-refresh" type="button" class="btn" style="font-size:10px;padding:2px 6px;">Refresh</button>
        </div>
        <div style="padding:8px 10px;">${validationHtml()}</div>
      </div>
    `;
    bindRightEvents();
  }

  function bindLeftEvents() {
    const q = (id) => leftHostEl.querySelector(id);
    q("[data-dismiss-error]")?.addEventListener("click", () => { error = null; renderLeft(); });
    q("#dt-name")?.addEventListener("input", (e) => { draftType.name = e.target.value; });
    q("#dt-mode")?.addEventListener("change", (e) => { draftType.storage_mode = e.target.value; });
    q("#dt-length")?.addEventListener("input", (e) => { draftType.length_ft = e.target.value; });
    q("#dt-width")?.addEventListener("input", (e) => { draftType.width_ft = e.target.value; });
    q("#dt-height")?.addEventListener("input", (e) => { draftType.height_ft = e.target.value; });
    q("#dt-weight")?.addEventListener("input", (e) => { draftType.max_weight_lbs = e.target.value; });
    q("#dt-tiers")?.addEventListener("input", (e) => { draftType.default_tier_count = e.target.value; });
    q("#dt-submit")?.addEventListener("click", submitDraftType);
    q("#dt-cancel")?.addEventListener("click", () => { editingTypeId = null; draftType = { name: "", storage_mode: "rack", length_ft: 24, width_ft: 10, height_ft: 9, max_weight_lbs: 8000, default_tier_count: 3 }; renderLeft(); });
    leftHostEl.querySelectorAll("[data-edit-type]").forEach((el) => el.addEventListener("click", () => startEditType(el.dataset.editType)));
    leftHostEl.querySelectorAll("[data-delete-type]").forEach((el) => el.addEventListener("click", () => deleteType(el.dataset.deleteType)));

    q("#rg-type")?.addEventListener("change", (e) => { rackGen.typeId = e.target.value; });
    q("#rg-rows")?.addEventListener("input", (e) => { rackGen.rows = e.target.value; });
    q("#rg-tiers")?.addEventListener("input", (e) => { rackGen.tiers = e.target.value; });
    q("#rg-rowgap")?.addEventListener("input", (e) => { rackGen.rowGapFt = e.target.value; });
    q("#rg-tiergap")?.addEventListener("input", (e) => { rackGen.tierGapFt = e.target.value; });
    q("#rg-building")?.addEventListener("input", (e) => { rackGen.building = e.target.value; });
    q("#rg-ox")?.addEventListener("input", (e) => { rackGen.originXFt = e.target.value; });
    q("#rg-oy")?.addEventListener("input", (e) => { rackGen.originYFt = e.target.value; });
    q("#rg-heading")?.addEventListener("input", (e) => { rackGen.headingDeg = e.target.value; });
    q("#rg-run")?.addEventListener("click", runRackGenerator);

    q("#pl-type")?.addEventListener("change", (e) => { placeTypeId = e.target.value; });
    q("#pl-toggle")?.addEventListener("click", () => { placing = !placing; renderLeft(); });

    q("#sel-dup-run")?.addEventListener("click", duplicateRun);
    q("#sel-dup-stack")?.addEventListener("click", duplicateStack);
    q("#sel-lock")?.addEventListener("click", toggleLockSelected);
    q("#sel-delete")?.addEventListener("click", deleteSelected);
  }

  function bindRightEvents() {
    rightHostEl.querySelector("[data-inspector-host]")?.querySelectorAll("[data-field]").forEach((el) => {
      const u = primaryUnit();
      if (!u) return;
      const commit = () => {
        const field = el.dataset.field;
        let value = el.value;
        if (el.type === "number") value = value === "" ? null : Number(value);
        saveInspectorField(u.unit_id, field, value);
      };
      el.addEventListener(el.tagName === "SELECT" ? "change" : "blur", commit);
    });
    rightHostEl.querySelector("#val-refresh")?.addEventListener("click", refreshRollups);
  }

  // ── lifecycle ────────────────────────────────────────────────────────────────────────────
  function onProjectChanged(e) {
    const id = String(e.detail?.projectId || "");
    if (id === currentMarinaId) return;
    currentMarinaId = id;
    disposeScene();
    unitTypes = []; units = []; summary = null; validation = null;
    selectedIds.clear(); primaryId = null; placing = false; editingTypeId = null;
    renderSkeleton();
    if (currentMarinaId) loadAll();
  }

  function mount(nextContainer) {
    containerEl = nextContainer;
    if (!currentMarinaId && _lastBroadcastProjectId) currentMarinaId = _lastBroadcastProjectId;
    document.addEventListener("hydrotools:project-changed", onProjectChanged);
    renderSkeleton();
    if (currentMarinaId) loadAll();
  }

  function destroy() {
    document.removeEventListener("hydrotools:project-changed", onProjectChanged);
    disposeScene();
    containerEl = null; canvasHostEl = null; leftHostEl = null; rightHostEl = null;
  }

  return { mount, destroy };
}

export const dryStorage3dTool = {
  id: TOOL_ID,
  name: "Dry Storage 3D",
  init(app) { this._rt = this._rt ?? createRuntime(app); },
  destroy() { this._rt?.destroy?.(); this._rt = null; },
  renderPanel(app, containerEl) {
    if (!this._rt) this._rt = createRuntime(app);
    this._rt.mount(containerEl);
  },
};
